
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import {
  CashOrderSendStatusEnum,
  ICashOrderDTO,
  IField,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
} from "@/entities";
import VueFilters from "@/vue-filters";
import Pagination from "@/components/common/Pagination.vue";

@Component({
  name: "MonitorsTable",
  mixins: [VueFilters],
  components: { Pagination },
})
export default class MonitorsTable extends Vue {
  @Prop({ required: true }) orders!: ICashOrderDTO[];
  @Prop({ required: false }) title!: string;
  @Prop({ required: false, default: false }) showSent!: boolean;
  isOrderSend = false;
  pageSize = 25;
  currentPage = 1;

  @Watch("showSent", { immediate: true })
  onShowSentChange(value: boolean): void {
    this.isOrderSend = value;
  }

  @Watch("orders")
  onOrdersChange(): void {
    this.currentPage = 1;
  }

  onChangeCurrentPage(page: number): void {
    this.currentPage = page;
  }

  get visibleFields(): IField[] {
    return this.fields.filter((field) => field.visible !== false);
  }

  get fields(): IField[] {
    return [
      {
        key: "actions-left",
        label: this.$t("column.actions") as string,
        sortable: false,
        thClass: "table-header pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "user.username",
        label: this.$t("column.createdBy") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "id",
        label: this.$t("column.order.idMPC") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "orderType",
        label: this.$t("column.orderType") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "servicePoint.description",
        label: this.$t("label.servicepoint") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "createdAt",
        label: this.$t("column.creation_date") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "sendStatusDescription",
        label: this.$t("column.sendStatus") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "retryCount",
        label: this.$t("column.retryCount") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "lastError",
        label: this.$t("column.lastError") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle last-error-cell",
        thStyle: { width: "180px", minWidth: "180px", maxWidth: "180px" },
      },
      {
        key: "sentAt",
        label: this.$t("column.sentAt") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
    ];
  }

  openDetail(order: ICashOrderDTO): void {
    this.$emit("open-detail", order);
  }

  onChangeVisibleSent(value: boolean): void {
    this.$emit("change-visible-sent", value);
  }

  resolveOrderType(productCode?: string): string {
    const orderType = resolveSolCustomerOrderType(productCode);
    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType);

    if (!descriptionKey) {
      return productCode || "";
    }

    return this.$t(descriptionKey).toString();
  }

  setColorBySendStatus(status: CashOrderSendStatusEnum): string {
    let color: string;
    switch (status) {
      case CashOrderSendStatusEnum.FAILED:
        color = "lightcoral";
        break;
      case CashOrderSendStatusEnum.PENDING_SENT:
        color = "white";
        break;
      case CashOrderSendStatusEnum.SENT:
        color = "lightgreen";
        break;
      case CashOrderSendStatusEnum.CANCELED:
        color = "lightgray";
        break;
    }
    return color;
  }

  cancelOrder(order: ICashOrderDTO): void {
    this.$emit("cancel-order", order);
  }

  reprocessOrder(order: ICashOrderDTO): void {
    this.$emit("reprocess-order", order);
  }

  isFailed(status: string): boolean {
    return status === CashOrderSendStatusEnum.FAILED;
  }

  isPendingToSend(status?: CashOrderSendStatusEnum): boolean {
    return status === CashOrderSendStatusEnum.PENDING_SENT;
  }
}
